import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  InputBase,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  InputLabel,
  useTheme,
  useMediaQuery,
  Checkbox,
  ListItemText,
  CircularProgress, // Import CircularProgress for loading spinner
  Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import nodatafound from '../../utilities/dummy_assets/No data-cuate 1.jpg';
import AdminReservationList from '../../utilities/api/reservation/AdminReservationList';
import { useSelector } from 'react-redux';
import { KeyboardArrowDown } from '@mui/icons-material';
import CustomPagination from '../CustomPagination/CustomPagination';
import CommonCardView from './CommonCardView';

export const dataGridStyle = {
  marginX: 2,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f7f7f7',
  },
  '& .MuiDataGrid-cell': {
    color: '#898989',
  },
  '& .header-style': {
    fontSize: '1.2rem',
    color: '#5C5C5C',
  },
  '& .MuiDataGrid-selectedRowCount': {
    visibility: 'hidden',
    width: 0,
    padding: 0,
    margin: 0,
    flex: '0 !important',
  },
  '& .MuiDataGrid-footerContainer': {
    paddingTop: '4px',
    paddingBottom: '4px',
    justifyContent: 'center',
  },
}

const SelectFilter = ({ label, value, options, onChange, sx = {} }) => (
  <FormControl sx={{ ...sx }}>
    <InputLabel>{label}</InputLabel>
    <Select
      multiple
      value={value}
      onChange={onChange}
      label={label}
      renderValue={selected => selected.join(', ')}
      IconComponent={KeyboardArrowDown}
      disabled={options?.length === 0}
      sx={{
        height: '3rem',
        width: '100%',
        border: '1px solid #F2F2F2',
        textAlign: 'left',
      }}
    >
      {options.map(option => (
        <MenuItem key={option} value={option}>
          <Checkbox checked={value.indexOf(option) > -1} />
          <ListItemText primary={option} />
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const ReservationsTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useSelector(state => state.user.user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [projects, setProjects] = useState([])
  // const [machines, setMachines] = useState([])
  // const [locations, setLocations] = useState([])
  const [approvalStatuses, setApprovalStatuses] = useState([]);
  // const [project, setProject] = useState([])
  // const [machine, setMachine] = useState([])
  // const [location, setLocation] = useState([])
  const [approvalStatus, setApprovalStatus] = useState(['Pending']);
  const [searchQuery, setSearchQuery] = useState('');

  const approvalOptions = useMemo(
    () => [
      { value: 'Pending', label: 'Pending' },
      { value: 'Approved', label: 'Approved' },
      { value: 'Rejected', label: 'Rejected' },
    ],
    []
  );

  const approvalMapping = useMemo(
    () => ({
      Pending: '0',
      Approved: '1',
      Rejected: '2',
      AutoApproved: '3',
      AutoRejected: '4',
    }),
    []
  );

  const fetchData = async () => {
    try {
      const data = await AdminReservationList.ReservationListGet();

      // Apply auto-approval logic for supervised machines
      const processedData = data.map(reservation => {
        // If machine is supervised (supervised = true), auto-approve the reservation
        if (
          reservation.supervised === true &&
          reservation.approved === 'Pending'
        ) {
          return {
            ...reservation,
            approved: 'Approved',
            approved_status: '1', // Set to approved status
          };
        }
        return reservation;
      });

      setRows(processedData.reverse());
      // setProjects([...new Set(processedData.map((item) => item.project_name))])
      // setMachines([...new Set(processedData.map((item) => item.name))])
      // setLocations([...new Set(processedData.map((item) => item.location))])
      setApprovalStatuses([
        ...new Set(processedData.map(item => item.approved)),
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  const handleStatusChange = useCallback(
    id => async event => {
      const newStatus = event.target.value;
      const newStatusValue = approvalMapping[newStatus];
      const originalRow = rows.find(row => row.id === id);
      const updatedRows = rows.map(row =>
        row.id === id
          ? { ...row, approved: newStatus, approved_status: newStatusValue }
          : row
      );
      setRows(updatedRows);

      try {
        await AdminReservationList.ReservationDetailPatch(id, {
          approved_status: newStatusValue,
        });
      } catch (error) {
        console.error(
          `Error updating detail for ID ${id}:`,
          error.response ? error.response.data : error.message
        );
        setRows(rows.map(row => (row.id === id ? originalRow : row)));
      }
    },
    [rows, approvalMapping]
  );

  const filteredRows = useMemo(() => {
    const lowerSearchQuery = searchQuery.toLowerCase();
    return rows.filter(row => {
      return (
        // (!project.length || project.includes(row.project_name)) &&
        // (!machine.length || machine.includes(row.name)) &&
        // (!location.length || location.includes(row.location)) &&
        (!approvalStatus.length || approvalStatus.includes(row.approved)) &&
        (!searchQuery ||
          row.reserver?.toLowerCase().includes(lowerSearchQuery) ||
          row.project_name?.toLowerCase().includes(lowerSearchQuery) ||
          row.reserved_date?.toLowerCase().includes(lowerSearchQuery) ||
          row.location?.toLowerCase().includes(lowerSearchQuery) ||
          row.name?.toLowerCase().includes(lowerSearchQuery) ||
          `${new Date(row.start_time).toLocaleString()} to ${new Date(
            row.end_time
          ).toLocaleString()}`
            .toLowerCase()
            .includes(lowerSearchQuery))
      );
    });
  }, [rows, approvalStatus, searchQuery]);

  const columns = useMemo(
    () => [
      {
        field: 'item_details',
        headerName: 'Item Details',
        flex: 1,
        headerAlign: 'center',
        align: 'left',
        headerClassName: 'header-style',
        sortable:false,
        renderCell: params => (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Box>
              <Typography variant="subtitle2" align="left" fontWeight="normal">
                {params.row?.name || 'No Text'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#007BFF', fontSize: '12px' }}
                align="left"
              >
                {params.row?.location || 'No Subtext'}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: 'reserver',
        headerName: 'Student Name',
        flex: 1,
        headerAlign: 'center',
        align: 'center',
        headerClassName: 'header-style',
      },
      {
        field: 'project_name',
        headerName: 'Project Name',
        flex: 1,
        headerAlign: 'center',
        align: 'center',
        headerClassName: 'header-style',
      },
      {
        field: 'start_time',
        headerName: 'From Time',
        flex: 1,
        headerClassName: 'header-style',
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ row }) => (
          <Box>
            <Typography variant="subtitle2" align="left" fontWeight="normal">
              {new Date(row.start_time).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </Typography>

            <Typography variant="body2" sx={{ fontSize: '12px' }} align="left">
              {new Date(row.start_time).toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'end_time',
        headerName: 'To Time',
        flex: 1,
        headerClassName: 'header-style',
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ row }) => (
          <Box>
            <Typography variant="subtitle2" align="left" fontWeight="normal">
              {new Date(row.end_time).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </Typography>

            <Typography variant="body2" sx={{ fontSize: '12px' }} align="left">
              {new Date(row.end_time).toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'action',
        headerName: 'Approval Status',
        sortable: false,
        width: 200,
        headerAlign: 'center',
        align: 'center',
        headerClassName: 'header-style',
        renderCell: ({ row }) => (
          <Select
            value={row.approved || ''}
            onChange={handleStatusChange(row.id)}
            displayEmpty
            sx={{
              width: '100%',
              borderRadius: '12px',
              height: '75%',
              textAlign: 'left',
              color: '#5C5C5C',
              fontSize: '14px',
            }}
            disabled={
              row.approved === 'Rejected' || row.approved === 'AutoRejected'
            }
            IconComponent={KeyboardArrowDown}
          >
            {approvalOptions.map(option => (
              <MenuItem key={option.value} value={option.label}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        ),
      },
    ],
    [handleStatusChange, approvalOptions]
  );

  return (
    <Box sx={{ height: '60%', width: '100%' }}>
      <Box
        component="form"
        onKeyDown={event => event.key === 'Enter' && event.preventDefault()}
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          mb: 3,
          mt: 1.5,
          boxSizing: 'border-box',
          paddingX: 2,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* <SelectFilter
          label="Select Project"
          value={project}
          options={projects}
          onChange={(event) => setProject(event.target.value)}
        />
        <SelectFilter
          label="Select Machine"
          value={machine}
          options={machines}
          onChange={(event) => setMachine(event.target.value)}
        />
        <SelectFilter
          label="Select Location"
          value={location}
          options={locations}
          onChange={(event) => setLocation(event.target.value)}
        /> */}
          <Grid item xs={12} sm={4}>
            <SelectFilter
              label="Select Approval Status"
              value={approvalStatus}
              options={approvalStatuses}
              onChange={event => setApprovalStatus(event.target.value)}
              sx={{ width: '100%' }}
            />
          </Grid>
          <Grid item xs={12} sm={4} ml="auto">
            <InputBase
              sx={{
                width: '100%',
                minHeight: '48px',
                border: '1px solid #F2F2F2',
                borderRadius: '10px',
                bgcolor: '#F7F7F7',
                '& .MuiInputBase-input': {
                  paddingLeft: '.1rem',
                },
              }}
              placeholder="Search…"
              inputProps={{ 'aria-label': 'search' }}
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <IconButton sx={{ paddingLeft: '10px' }} aria-label="search">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Grid>
        </Grid>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress
            size={60}
            thickness={4.5}
            sx={{ color: '#273F77' }} // Customize color to match your theme
          />
          <Typography
            variant="h6"
            sx={{
              color: '#273F77',
              fontFamily: 'Inter',
              fontWeight: 500,
              marginTop: 2,
            }}
          >
            Loading...
          </Typography>
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            marginTop: '100px',
            padding: '32px 24px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '21px',
          }}
        >
          <Box
            component="img"
            src={nodatafound}
            sx={{ width: 211.889, height: 196.824, flexShrink: 0 }}
          />
          <Typography
            sx={{
              color: '#273F77',
              textAlign: 'center',
              fontFamily: 'Inter',
              fontSize: 26,
              fontWeight: 500,
              lineHeight: '150%',
              letterSpacing: '-0.352px',
            }}
          >
            No Records Found!
          </Typography>
        </Box>
      ) : isMobile ? (
        <CommonCardView rows={filteredRows} fields={columns} />
      ) : (
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pagination
          pageSizeOptions={[7, 10, 25]}
          initialState={{
            pagination: { paginationModel: { pageSize: 7 } },
          }}
          paginationMode="client"
          disableSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          disableDensitySelector
          disableExport
          disableMultipleColumnsSorting
          // sortingOrder={[]}
          // disableColumnSort
          slots={{
            pagination: CustomPagination,
          }}
          sx={dataGridStyle}
        />
      )}
    </Box>
  );
};

export default ReservationsTable;
